import {
  NAVBAR_TOPICS_TO_HIDE_WIDTH,
  PORTRAIT_TABLET_MAX_WIDTH
} from "@/src/common/config"
import { DESKTOP_PREFERRED_MORE_MENU_COPY } from "@/src/sb/business-layer/config"
import { NavigationMenuItem } from "@/src/sb/business-layer/types"
import MenuDropdown from "@/src/sb/components/Navigationbar/MenuDropdown"
import AnimatedArrow from "@/src/sb/components/icon/AnimatedArrow"
import { TOPICS_V2 } from "@/src/sb/constants/featureFlags"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import classNames from "classnames"
import { groupBy } from "lodash"
import React, { useState } from "react"
import { createPortal } from "react-dom"

import { HELP_LINK, V2_FEATURE_TOGGLES } from "../../constants/configs"
import { pages } from "../../constants/copy"
import { SearchInput } from "../Input"
import Logo from "../Logo"
import NavItem from "../NavItem"
import { SimpleLink } from "../SimpleLink"
import Spinner from "../icon/Spinner"
import { Accounts } from "./Accounts"
import { MenuItem } from "./MobileNavbar/MenuItem"
import { DesktopTopNavProps } from "./types"

type MoreMenuProps = {
  menu: NavigationMenuItem[]
  onTopicClick?: (
    menuItem: Pick<
      NavigationMenuItem,
      "label" | "destination" | "category_name"
    >
  ) => void
}

const Menu = ({
  label,
  renderDropdown
}: {
  label: string
  renderDropdown: (onClose: () => void) => JSX.Element
}) => {
  const [, currentWidth] = useWindowSize()
  const [showMenu, setShowMenu] = useState(false)

  return (
    <section className={"inline-block"}>
      <button
        type="button"
        className={
          "inline-flex items-center whitespace-nowrap min-w-max py-2 px-4 hover:bg-gray-50 font-normal text-base"
        }
        onClick={() => setShowMenu(!showMenu)}
        aria-expanded={showMenu}
        aria-haspopup={true}
        aria-hidden={!showMenu}
        aria-label={label}
      >
        {label}
        <AnimatedArrow
          position={showMenu ? "up" : "down"}
          aria-hidden={!showMenu}
        />
      </button>

      {/* 1024, gotten from "screens.portrait_tablet" modifier*/}
      {showMenu && currentWidth > PORTRAIT_TABLET_MAX_WIDTH
        ? createPortal(
            renderDropdown(() => setShowMenu(false)),
            document.querySelector("#reactPortal")!
          )
        : null}
    </section>
  )
}

const MoreMenu = (props: MoreMenuProps) => {
  return (
    <Menu
      label={DESKTOP_PREFERRED_MORE_MENU_COPY}
      renderDropdown={(onClose) => (
        <MenuDropdown
          categorizeMenuItem
          onClose={onClose}
          onMenuItemClick={({ label, destination, category_name }) =>
            props.onTopicClick?.({
              label,
              destination,
              category_name
            })
          }
          menu={props.menu.map((menuItem) => ({
            ...menuItem,
            href: menuItem.destination
          }))}
        />
      )}
    />
  )
}

export const DesktopTopNav = ({
  collapseSearchInput,
  ...props
}: DesktopTopNavProps) => {
  const {
    userType,
    onSearchChange,
    username = "Account",
    searchText,
    showSearch = false,
    navigationMenu = [],
    loading,
    onNavItemClick,
    onClear,
    setNextQueryParam
  } = props
  const [showNavbarMenu, setShowNavbarMenu] = useState(true)
  const [, width] = useWindowSize()

  const navItemClick = (name: string, props?: Record<string, any>) => () => {
    onNavItemClick?.(name, { ...props })
  }

  const menuItemsWithCategoryName: Array<NavigationMenuItem> =
    navigationMenu.filter(({ category_name }) => Boolean(category_name))

  const navbarMenu: Array<NavigationMenuItem> = []
  const navbarSubMenu: Array<NavigationMenuItem> = []
  const moreMenuItems: Array<NavigationMenuItem> = []
  let navbarSubMenuHashMap: Record<string, Array<NavigationMenuItem>> = {}

  const navbarMenuLabelToHide = menuItemsWithCategoryName
    .filter((menuItem) => menuItem.priority === "low")
    .map((menuItem) => menuItem.label)

  for (const menuItem of menuItemsWithCategoryName) {
    const hideMenuItemOnSmallerScreen =
      navbarMenuLabelToHide.includes(menuItem.label) &&
      width < NAVBAR_TOPICS_TO_HIDE_WIDTH

    const parentTopicWouldHideOnSmallerScreen =
      navbarMenuLabelToHide.includes(
        menuItemsWithCategoryName.find(
          (menuItemWithCategoryName) =>
            menuItemWithCategoryName.id === menuItem.parent_id
        )?.label ?? ""
      ) && width < NAVBAR_TOPICS_TO_HIDE_WIDTH

    if (
      (menuItem.level === "subnavigation" &&
        (!menuItem.parent_id || parentTopicWouldHideOnSmallerScreen)) ||
      hideMenuItemOnSmallerScreen
    ) {
      if (menuItem.more_topics_visibility_by_screen.includes("desktop"))
        moreMenuItems.push(menuItem)

      continue
    }

    if (menuItem.level === "subnavigation" && menuItem.parent_id) {
      navbarSubMenu.push(menuItem)

      continue
    }

    navbarMenu.push(menuItem)
  }

  navbarSubMenuHashMap = groupBy<NavigationMenuItem>(navbarSubMenu, "parent_id")

  return (
    <nav className="bg-stears_white flex items-center justify-between h-full min-h-[64px] w-full px-6 border-b border-border-10">
      <div className="flex gap-8 w-full">
        <div>
          <SimpleLink href={pages.home} onClick={navItemClick("logo")}>
            <Logo type="black" />
          </SimpleLink>
        </div>

        <section className={"grow"}>
          <div
            className={classNames(
              "gap-4 items-center justify-center transition-all ease-in-out",
              [showNavbarMenu ? "inline-flex" : "hidden"]
            )}
          >
            {navbarMenu.map(({ label, id, destination, category_name }) => {
              const subMenu: Array<{ href: string } & NavigationMenuItem> = (
                navbarSubMenuHashMap[id] ?? []
              ).map((menuItem) => {
                return {
                  ...menuItem,
                  label: menuItem.label,
                  href: menuItem.destination
                }
              })

              return (
                <div
                  className={classNames(
                    navbarMenuLabelToHide.includes(label)
                      ? "hidden xl:inline-block"
                      : "inline-block"
                  )}
                  key={label}
                >
                  {id in navbarSubMenuHashMap ? (
                    <NavItem.Dropdown
                      columns="one"
                      type="simple"
                      label={label}
                      menuAlign="left"
                    >
                      <MenuItem<Pick<NavigationMenuItem, "category_name">>
                        size="lg"
                        menuItems={subMenu}
                        onItemClick={({ href, label, category_name }) => {
                          navItemClick("menu_item", {
                            label,
                            destination: href,
                            category_name,
                            context: "navbar"
                          })()
                        }}
                      />
                    </NavItem.Dropdown>
                  ) : (
                    <NavItem.Link
                      href={destination}
                      onClick={navItemClick("menu_item", {
                        label,
                        destination,
                        category_name: category_name,
                        context: "navbar"
                      })}
                    >
                      {label}
                    </NavItem.Link>
                  )}
                </div>
              )
            })}

            {navigationMenu?.length ? (
              <MoreMenu
                menu={moreMenuItems}
                onTopicClick={({ label, destination, category_name }) =>
                  navItemClick("topic", {
                    label,
                    destination,
                    category_name,
                    context: "more_menu"
                  })()
                }
              />
            ) : null}

            {!V2_FEATURE_TOGGLES.has(TOPICS_V2) ? (
              <NavItem.Link href={HELP_LINK} onClick={navItemClick("help")}>
                Help
              </NavItem.Link>
            ) : null}
          </div>
        </section>
        <div className="flex items-center">
          <section className={""}>
            {showSearch && (
              <form
                action={pages.search}
                className="mx-4"
                style={{ display: showSearch ? "black" : "none" }}
              >
                <SearchInput
                  placeholder="Search"
                  ariaLabel="Search"
                  name="q"
                  required
                  collapsed={collapseSearchInput}
                  value={searchText}
                  onChange={onSearchChange}
                  onClear={onClear}
                  onSearchInputOpen={() => setShowNavbarMenu(false)}
                  onSearchInputClose={() => setShowNavbarMenu(true)}
                />
              </form>
            )}
          </section>

          {loading && <Spinner />}

          {!loading && (
            <Accounts
              name={username}
              userType={userType}
              onClick={onNavItemClick}
              setNextQueryParam={setNextQueryParam}
            />
          )}
        </div>
      </div>
    </nav>
  )
}

export default DesktopTopNav
